import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LeadBusinessModal } from '../components/leads/LeadBusinessModal';
import {
  CreateListModal,
  ExportModal,
  ListsActionModal,
} from '../components/leads/LeadBulkModals';
import { targetLabel } from '../lib/lead-target';
import { LeadDrawer } from '../components/leads/LeadDrawer';
import { LeadFilterPanel } from '../components/leads/LeadFilterPanel';
import { NewLeadModal } from '../components/leads/NewLeadModal';
import { ViewsMenu } from '../components/leads/ViewsMenu';
import { Menu } from '../components/Menu';
import {
  Avatar,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  KindBadge,
  Loading,
  TagPill,
  inputClass,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import {
  Mail,
  Menu as MenuIcon,
  MoreHorizontal,
  Phone,
  Plus,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { formatMoney } from '../lib/format';
import {
  FIELD_OPERATOR_LABELS,
  OPERATOR_LABELS,
  activeFilterCount,
  parseFilters,
  serializeFilters,
  type LeadFilterState,
} from '../lib/lead-filter-model';
import {
  apiAssetUrl,
  leadActions,
  listLeadsByQuery,
  lists as listsApi,
  previewLeads,
  tags as tagsApi,
  type LeadTarget,
} from '../lib/resources';
import type { Lead } from '../types';

const PAGE_SIZE = 50;
const LONG_DATE = new Intl.DateTimeFormat('pt-BR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const daysAgo = (iso: string | null) =>
  iso ? Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 86_400_000)) : null;

type BulkModal = 'add-lists' | 'remove-lists' | 'create-list' | 'export' | 'delete' | null;

/**
 * Leads (/leads) — doc 02, seção 1: busca, Visões, Filtros na URL, chips,
 * rolagem contínua, seleção e o menu ≡ com as ações em massa (sempre confirmadas).
 * A ficha abre em drawer por cima (`?lead=<id>`).
 */
export function LeadsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { can, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [searchDraft, setSearchDraft] = useState(params.get('search') ?? '');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // A seleção vale para a lista em que foi feita: mudou o filtro, ela some.
  const [selection, setSelection] = useState<{ query: string; ids: string[] }>({
    query: '',
    ids: [],
  });
  const [bulk, setBulk] = useState<BulkModal>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [newBusinessFor, setNewBusinessFor] = useState<Lead | null>(null);
  const [deleting, setDeleting] = useState<Lead | null>(null);

  const filters = useMemo(() => parseFilters(params), [params]);
  const viewId = params.get('viewId');
  const openLeadId = params.get('lead');
  const filterQuery = serializeFilters(filters, viewId ? { viewId } : {});
  const canWrite = can('leads_admin', 'leads_member_operator');
  const canDelete = can('leads_admin');
  const canExport = isAdmin || (can('leads_admin') && can('allow-lead-export'));

  // Busca com espera curta: não dispara uma requisição por tecla.
  useEffect(() => {
    const handle = setTimeout(() => {
      if ((params.get('search') ?? '') === searchDraft) return;
      const next = new URLSearchParams(params);
      if (searchDraft) next.set('search', searchDraft);
      else next.delete('search');
      setParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(handle);
  }, [searchDraft, params, setParams]);

  const selected = selection.query === filterQuery ? selection.ids : [];
  const setSelected = (update: string[] | ((current: string[]) => string[])) =>
    setSelection({
      query: filterQuery,
      ids: typeof update === 'function' ? update(selected) : update,
    });

  const leadsQuery = useInfiniteQuery({
    queryKey: ['leads', 'list', filterQuery],
    queryFn: ({ pageParam }) => listLeadsByQuery(filterQuery, pageParam, PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.count ? loaded : undefined;
    },
  });
  const leads = leadsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const count = leadsQuery.data?.pages[0]?.count ?? 0;

  // Rolagem contínua: carrega mais quando o fim da tabela aparece.
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0]?.isIntersecting &&
        leadsQuery.hasNextPage &&
        !leadsQuery.isFetchingNextPage
      ) {
        void leadsQuery.fetchNextPage();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [leadsQuery]);

  const tags = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list() });
  const lists = useQuery({ queryKey: ['lists'], queryFn: () => listsApi.list() });

  const target: LeadTarget = selected.length ? { ids: selected } : { filter: filterQuery };
  const preview = useQuery({
    queryKey: ['leads', 'preview', filterQuery],
    queryFn: () => previewLeads(filterQuery),
    enabled: bulk !== null && selected.length === 0,
  });
  const targetCount = selected.length ? selected.length : (preview.data?.count ?? null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    queryClient.invalidateQueries({ queryKey: ['trash'] });
  };

  const bulkDelete = useMutation({
    mutationFn: () => leadActions.remove(target),
    onSuccess: (result) => {
      setBulk(null);
      setSelected([]);
      refresh();
      setFeedback(
        'job' in result
          ? 'Exclusão grande criada como execução — acompanhe em Configurações › Execuções.'
          : `${result.deleted} lead(s) enviados para a Lixeira.`,
      );
    },
  });
  const singleDelete = useMutation({
    mutationFn: (lead: Lead) => leadActions.remove({ ids: [lead.id] }),
    onSuccess: () => {
      setDeleting(null);
      refresh();
    },
  });

  const setFilters = (next: LeadFilterState) => {
    const query = serializeFilters(next, viewId ? { viewId } : {});
    setParams(new URLSearchParams(query));
  };
  const openLead = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('lead', id);
    else next.delete('lead');
    setParams(next);
  };

  const chips = buildChips(filters, {
    tag: (id) => tags.data?.data.find((tag) => tag.id === id)?.name,
    list: (id) => lists.data?.data.find((list) => list.id === id)?.name,
  });

  const allSelected = leads.length > 0 && leads.every((lead) => selected.includes(lead.id));

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500">
            Consulte, crie, modifique ou remova seus leads
          </p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus size={15} /> Novo Lead
          </Button>
        )}
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="relative w-full max-w-sm">
          <Search
            size={15}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            className={`${inputClass} mt-0 pl-8`}
            placeholder="Pesquisar..."
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>
        <span className="text-sm text-slate-500">
          {selected.length
            ? `${selected.length} registro(s) selecionado(s)`
            : `${count} resultado(s)`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <ViewsMenu
            activeViewId={viewId}
            currentQuery={serializeFilters(filters)}
            onSelect={(view) => setParams(new URLSearchParams(view ? `viewId=${view.id}` : ''))}
          />
          <Button onClick={() => setFiltersOpen(true)}>
            <SlidersHorizontal size={14} /> Filtros
            {activeFilterCount(filters) > 0 && ` (${activeFilterCount(filters)})`}
          </Button>
          <Menu
            trigger={(toggle) => (
              <Button onClick={toggle} aria-label="Ações">
                <MenuIcon size={15} />
              </Button>
            )}
            groups={[
              {
                title: 'Importações',
                items: [
                  {
                    label: 'Importar leads/negócios',
                    onSelect: () => navigate('/leads/importar'),
                    disabled: !canWrite,
                  },
                  {
                    label: 'Exportar leads',
                    onSelect: () => setBulk('export'),
                    disabled: !canExport,
                  },
                ],
              },
              {
                title: 'Listas',
                items: [
                  {
                    label: 'Adicionar às listas',
                    onSelect: () => setBulk('add-lists'),
                    disabled: !canWrite || !selected.length,
                  },
                  {
                    label: 'Criar nova lista',
                    onSelect: () => setBulk('create-list'),
                    disabled: !canWrite || !selected.length,
                  },
                  {
                    label: 'Remover da lista',
                    onSelect: () => setBulk('remove-lists'),
                    disabled: !canWrite || !selected.length,
                  },
                ],
              },
              {
                title: 'Exclusões',
                items: [
                  {
                    label: selected.length
                      ? 'Excluir leads selecionados'
                      : 'Excluir todos leads',
                    hint: selected.length ? undefined : 'Todos os do filtro atual',
                    danger: true,
                    disabled: !canDelete,
                    onSelect: () => setBulk('delete'),
                  },
                ],
              },
            ]}
          />
        </div>
      </div>

      {(chips.length > 0 || viewId) && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {viewId && (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-xs text-violet-700">
              📁 Visão aplicada
              <button
                type="button"
                onClick={() => setParams(new URLSearchParams(serializeFilters(filters)))}
              >
                ×
              </button>
            </span>
          )}
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
            >
              {chip.label}
              <button
                type="button"
                aria-label={`Tirar ${chip.label}`}
                onClick={() => setFilters(chip.remove())}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {feedback && (
        <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {feedback}{' '}
          <button
            type="button"
            className="ml-2 text-xs underline"
            onClick={() => setFeedback(null)}
          >
            ok
          </button>
        </p>
      )}

      {leadsQuery.error && <ErrorNote>{errorMessage(leadsQuery.error)}</ErrorNote>}

      {leadsQuery.isLoading ? (
        <Loading />
      ) : leads.length === 0 ? (
        <EmptyState title="Nenhum lead encontrado" hint="Ajuste a busca ou os filtros." />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-10 px-4 py-2">
                  <input
                    type="checkbox"
                    aria-label="Selecionar a página"
                    checked={allSelected}
                    onChange={() =>
                      setSelected(allSelected ? [] : leads.map((lead) => lead.id))
                    }
                  />
                </th>
                <th className="px-3 py-2">Nome</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Contatos</th>
                <th className="px-3 py-2">Tags</th>
                <th className="px-3 py-2">Dados</th>
                <th className="px-3 py-2">Data de criação</th>
                <th className="w-10 px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  selected={selected.includes(lead.id)}
                  onToggle={() =>
                    setSelected((current) =>
                      current.includes(lead.id)
                        ? current.filter((id) => id !== lead.id)
                        : [...current, lead.id],
                    )
                  }
                  onOpen={() => openLead(lead.id)}
                  onNewBusiness={canWrite ? () => setNewBusinessFor(lead) : undefined}
                  onDelete={canDelete ? () => setDeleting(lead) : undefined}
                />
              ))}
            </tbody>
          </table>
          <div ref={sentinel} className="py-3 text-center text-xs text-slate-400">
            {leadsQuery.isFetchingNextPage
              ? 'Carregando mais…'
              : leadsQuery.hasNextPage
                ? ''
                : `${count} lead(s)`}
          </div>
        </div>
      )}

      {filtersOpen && (
        <LeadFilterPanel
          initial={filters}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => {
            setFilters(next);
            setFiltersOpen(false);
          }}
        />
      )}
      {creating && (
        <NewLeadModal
          onClose={() => setCreating(false)}
          onCreated={(lead, open) => {
            refresh();
            if (open) openLead(lead.id);
          }}
        />
      )}
      {openLeadId && <LeadDrawer leadId={openLeadId} onClose={() => openLead(null)} />}
      {newBusinessFor && (
        <LeadBusinessModal
          lead={newBusinessFor}
          onClose={() => setNewBusinessFor(null)}
          onCreated={() => {
            setNewBusinessFor(null);
            refresh();
          }}
        />
      )}

      {(bulk === 'add-lists' || bulk === 'remove-lists') && (
        <ListsActionModal
          mode={bulk === 'add-lists' ? 'add' : 'remove'}
          target={target}
          count={targetCount}
          onClose={() => setBulk(null)}
          onDone={(affected) => {
            setBulk(null);
            refresh();
            setFeedback(`${affected} lead(s) atualizados.`);
          }}
        />
      )}
      {bulk === 'create-list' && (
        <CreateListModal
          target={target}
          count={targetCount}
          onClose={() => setBulk(null)}
          onDone={(affected) => {
            setBulk(null);
            queryClient.invalidateQueries({ queryKey: ['lists'] });
            refresh();
            setFeedback(`Lista criada com ${affected} lead(s).`);
          }}
        />
      )}
      {bulk === 'export' && (
        <ExportModal
          filter={serializeFilters(filters, viewId ? { viewId } : {})}
          count={preview.data?.count ?? null}
          onClose={() => setBulk(null)}
        />
      )}
      {bulk === 'delete' && (
        <ConfirmDialog
          title="Excluir leads?"
          message={
            <>
              <p>
                <strong>{targetLabel(target, targetCount)}</strong> vão para a Lixeira, com os
                negócios e as atividades de cada um.
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Dá para restaurar em Configurações › Lixeira por 30 dias. Acima de 200 leads a
                exclusão roda em segundo plano.
              </p>
            </>
          }
          confirmLabel="Excluir"
          danger
          busy={bulkDelete.isPending || (selected.length === 0 && preview.isLoading)}
          error={bulkDelete.error ? errorMessage(bulkDelete.error) : null}
          onConfirm={() => bulkDelete.mutate()}
          onClose={() => setBulk(null)}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title="Excluir lead?"
          message={
            <p>
              <strong>{deleting.name}</strong> vai para a Lixeira com os negócios e as
              atividades.
            </p>
          }
          confirmLabel="Excluir"
          danger
          busy={singleDelete.isPending}
          error={singleDelete.error ? errorMessage(singleDelete.error) : null}
          onConfirm={() => singleDelete.mutate(deleting)}
          onClose={() => setDeleting(null)}
        />
      )}
      <p className="mt-4 text-xs text-slate-400">
        Dica: a URL guarda os filtros — dá para compartilhar o link.{' '}
        <Link to="/configuracoes/execucoes" className="underline">
          Execuções
        </Link>
      </p>
    </div>
  );
}

function LeadRow({
  lead,
  selected,
  onToggle,
  onOpen,
  onNewBusiness,
  onDelete,
}: {
  lead: Lead;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onNewBusiness?: () => void;
  onDelete?: () => void;
}) {
  const avatar = lead.image?.startsWith('/api/') ? apiAssetUrl(lead.image) : lead.image;
  const lastPurchase = daysAgo(lead.metrics?.lastPurchaseDate ?? null);
  return (
    <tr className={`hover:bg-slate-50 ${selected ? 'bg-violet-50/40' : ''}`}>
      <td className="px-4 py-2.5">
        <input
          type="checkbox"
          aria-label={`Selecionar ${lead.name}`}
          checked={selected}
          onChange={onToggle}
        />
      </td>
      <td className="px-3 py-2.5">
        <button type="button" onClick={onOpen} className="flex items-center gap-2.5 text-left">
          <Avatar name={lead.name} src={avatar} />
          <span className="min-w-0">
            <span className="block font-semibold text-slate-900 hover:underline">
              {lead.name}
            </span>
            <span className="block text-xs text-slate-400">
              Ticket médio {formatMoney(lead.metrics?.averageTicket)}
            </span>
          </span>
        </button>
      </td>
      <td className="px-3 py-2.5">
        <KindBadge kind={lead.kind} />
      </td>
      <td className="px-3 py-2.5 text-slate-600">
        {lead.phone || lead.email ? (
          <span className="flex items-center gap-1.5">
            {lead.phone ? (
              <Phone size={13} className="shrink-0 text-slate-400" />
            ) : (
              <Mail size={13} className="shrink-0 text-slate-400" />
            )}
            {lead.phone ?? lead.email}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-1">
          {lead.tags.slice(0, 2).map((tag) => (
            <TagPill key={tag.id} tag={tag} />
          ))}
          {lead.tags.length > 2 && (
            <span className="text-xs text-slate-400">+{lead.tags.length - 2}</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-600">
        <span className="flex items-center gap-2">
          <span>Total {formatMoney(lead.metrics?.totalSpent)}</span>
          <span
            className="flex h-5 min-w-5 items-center justify-center rounded-full border border-slate-300 px-1 text-[10px] font-semibold text-slate-600"
            title="Compras"
          >
            {lead.metrics?.purchaseCount ?? 0}
          </span>
        </span>
        <span className="text-slate-400">
          {lead.metrics?.purchaseFrequency
            ? `Ciclo ${Math.round(lead.metrics.purchaseFrequency)}d`
            : 'Sem ciclo'}
          {lastPurchase !== null && (
            <span className="text-red-500"> · Última {lastPurchase}d</span>
          )}
        </span>
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-500">
        {LONG_DATE.format(new Date(lead.createdAt))}
      </td>
      <td className="px-3 py-2.5">
        <Menu
          trigger={(toggle) => (
            <button
              type="button"
              onClick={toggle}
              className="rounded p-1 text-slate-400 hover:bg-slate-100"
              aria-label="Ações do lead"
            >
              <MoreHorizontal size={16} />
            </button>
          )}
          groups={[
            {
              items: [
                { label: 'Editar', hint: 'Abrir a ficha do lead', onSelect: onOpen },
                ...(onNewBusiness
                  ? [
                      {
                        label: 'Criar negócio',
                        hint: 'Crie um negócio para o lead',
                        onSelect: onNewBusiness,
                      },
                    ]
                  : []),
                ...(onDelete
                  ? [
                      {
                        label: 'Excluir',
                        hint: 'Vai para a Lixeira',
                        danger: true,
                        onSelect: onDelete,
                      },
                    ]
                  : []),
              ],
            },
          ]}
        />
      </td>
    </tr>
  );
}

/** Um chip por filtro ativo ("Tags · contém algum · Tag A"), com o que o × tira. */
function buildChips(
  state: LeadFilterState,
  names: { tag: (id: string) => string | undefined; list: (id: string) => string | undefined },
): Array<{ key: string; label: string; remove: () => LeadFilterState }> {
  const chips: Array<{ key: string; label: string; remove: () => LeadFilterState }> = [];
  const without = (key: keyof LeadFilterState) => () => {
    const next = { ...state };
    delete next[key];
    return next;
  };
  const idChip = (
    key: keyof LeadFilterState,
    title: string,
    name?: (id: string) => string | undefined,
  ) => {
    const value = state[key] as
      { operator: keyof typeof OPERATOR_LABELS; ids: string[]; empty?: boolean } | undefined;
    if (!value) return;
    const detail = value.empty
      ? `sem ${title.toLowerCase()}`
      : `${OPERATOR_LABELS[value.operator]} · ${
          name
            ? value.ids.map((id) => name(id) ?? '…').join(', ')
            : `${value.ids.length} item(ns)`
        }`;
    chips.push({ key, label: `${title} · ${detail}`, remove: without(key) });
  };

  idChip('tags', 'Tags', names.tag);
  idChip('lists', 'Listas', names.list);
  idChip('pipelines', 'Pipelines');
  idChip('stages', 'Etapas');
  idChip('products', 'Produtos');
  idChip('attendants', 'Atendente');
  idChip('companies', 'Empresa');
  if (state.kind)
    chips.push({
      key: 'kind',
      label: `Tipo · ${state.kind === 'COMPANY' ? 'Empresa' : 'Pessoa'}`,
      remove: without('kind'),
    });
  if (state.source?.length)
    chips.push({
      key: 'source',
      label: `Origem · contém ${state.source.join(' ou ')}`,
      remove: without('source'),
    });
  if (state.jobTitles?.length)
    chips.push({
      key: 'jobTitles',
      label: `Cargo · ${state.jobTitles.join(', ')}`,
      remove: without('jobTitles'),
    });
  const address = [
    state.addressCountry,
    state.addressState,
    state.addressCity,
    state.addressBlock,
  ].filter(Boolean);
  if (address.length) {
    chips.push({
      key: 'address',
      label: `Endereço · ${address.join(', ')}`,
      remove: () => ({
        ...state,
        addressCountry: undefined,
        addressState: undefined,
        addressCity: undefined,
        addressBlock: undefined,
      }),
    });
  }
  (state.additionalFields ?? []).forEach((field, index) =>
    chips.push({
      key: `field-${index}`,
      label: `Campo adicional · ${FIELD_OPERATOR_LABELS[field.operator]} ${field.value}`,
      remove: () => ({
        ...state,
        additionalFields: state.additionalFields?.filter((_, position) => position !== index),
      }),
    }),
  );
  if (state.createdAtFrom || state.createdAtTo) {
    const format = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '…');
    chips.push({
      key: 'created',
      label: `Criação · ${format(state.createdAtFrom)} a ${format(state.createdAtTo)}`,
      remove: () => ({ ...state, createdAtFrom: undefined, createdAtTo: undefined }),
    });
  }
  if (state.lastPurchaseMin || state.lastPurchaseMax) {
    chips.push({
      key: 'lastPurchase',
      label: `Última compra · ${state.lastPurchaseMin ?? 0} a ${state.lastPurchaseMax ?? '∞'} dias`,
      remove: () => ({ ...state, lastPurchaseMin: undefined, lastPurchaseMax: undefined }),
    });
  }
  if (state.businessesCountMin || state.businessesCountMax) {
    chips.push({
      key: 'businessesCount',
      label: `Negócios · ${state.businessesCountMin ?? 0} a ${state.businessesCountMax ?? '∞'}`,
      remove: () => ({
        ...state,
        businessesCountMin: undefined,
        businessesCountMax: undefined,
      }),
    });
  }
  return chips;
}
